import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLatestNews } from '../services/wordpress';
import { NewsItem } from '../types';
import { Calendar, ArrowRight, Loader2, PlusCircle, AlertTriangle } from 'lucide-react';
import { safeHTML, stripHTML } from '../utils/safeHTML';
import './NewsSection.css';

const NewsSection: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadNews(1);
  }, []);

  async function loadNews(pageNum: number) {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    const newData = await getLatestNews(pageNum, 3); // Fetch 3 per request
    
    if (newData.length === 0) {
        setHasMore(false);
    } else {
        if (pageNum === 1) {
            setNews(newData);
        } else {
            setNews(prev => [...prev, ...newData]);
        }
    }

    setLoading(false);
    setLoadingMore(false);
  }

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadNews(nextPage);
  };

  return (
    <section className="NewsSection_section">
      <div className="container">
        <div className="NewsSection_header">
          <div>
            <span className="NewsSection_label">Kabar Lakpesdam</span>
            <h2 className="NewsSection_title">Berita & Opini Terkini</h2>
          </div>
          <button className="NewsSection_indexBtn" onClick={() => navigate('/berita')}>
            Indeks Berita <ArrowRight size={18} />
          </button>
        </div>

        {loading ? (
            <div className="NewsSection_grid" role="status">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="NewsSection_card" style={{ border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ width: '100%', height: '250px', backgroundColor: '#e2e8f0', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                    <div className="NewsSection_content" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div style={{ width: '30%', height: '16px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                      <div style={{ width: '90%', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                      <div style={{ width: '70%', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
                      <div style={{ width: '100%', height: '60px', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite', marginTop: '0.5rem' }} />
                    </div>
                  </div>
                ))}
                <span className="sr-only">Memuat berita...</span>
                <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }`}</style>
            </div>
        ) : (
            <>
                <div className="NewsSection_grid">
                {news.map((item) => (
                    <div 
                      key={item.id} 
                      className="NewsSection_card"
                      onClick={() => navigate(`/${item.slug || item.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/${item.slug || item.id}`);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Baca berita: ${stripHTML(item.title)}`}
                      style={{ cursor: 'pointer' }}
                    >
                    <div className="NewsSection_imgWrapper">
                        <div className="NewsSection_imgGradient"></div>
                        <img 
                        src={item.imageUrl} 
                        alt={item.title} 
                        className="NewsSection_img"
                        loading="lazy"
                        width="400"
                        height="250"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?fit=crop&w=400&h=400';
                        }}
                        />
                        <div className="NewsSection_categoryBadge">
                        {item.category}
                        </div>
                        {item.isFallback && (
                            <div style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: 'rgba(255, 255, 255, 0.9)', color: '#d97706', padding: '0.25rem 0.5rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <AlertTriangle size={14} /> Mode Offline
                            </div>
                        )}
                    </div>
                    
                    <div className="NewsSection_content">
                        <div className="NewsSection_meta">
                        <span className="NewsSection_metaItem"><Calendar size={14} /> {item.date}</span>
                        </div>
                        
                        {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                        <h3 className="NewsSection_cardTitle" {...safeHTML(item.title)} aria-label={item.title.replace(/<[^>]+>/g, '').trim()} />
                        
                        <div 
                            className="NewsSection_excerpt"
                            {...safeHTML(item.excerpt)}
                        />
                        
                        <div className="NewsSection_footer">
                            <span className="NewsSection_readMore">
                                Baca Selengkapnya <ArrowRight size={16} className="NewsSection_readMoreIcon" />
                            </span>
                        </div>
                    </div>
                    </div>
                ))}
                </div>

                {/* Tombol Load More */}
                {hasMore && news.length > 0 && (
                    <div className="flex justify-center mt-10">
                        <button 
                            onClick={handleLoadMore} 
                            disabled={loadingMore}
                            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                                loadingMore 
                                ? 'bg-slate-200 text-slate-500 cursor-not-allowed' 
                                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                            }`}
                        >
                            {loadingMore ? (
                                <>
                                    <Loader2 className="animate-spin" size={20} /> Memuat...
                                </>
                            ) : (
                                <>
                                    <PlusCircle size={20} /> Muat Berita Lama
                                </>
                            )}
                        </button>
                    </div>
                )}
            </>
        )}
        
        <div className="NewsSection_mobileAction">
            <button className="NewsSection_btnMobile" onClick={() => navigate('/berita')}>
                Lihat Semua Berita
            </button>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;

